import { ReactElement, useEffect, useState } from "react";
import {
  Chip,
  FormControl,
  IconButton, Input,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import { IDataSchema, ICellFilter } from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../configuration/configuration";
import Select  from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";

interface IProps {
  schema: IDataSchema;
  filters: ICellFilter[];
  updateFilter: (filter: ICellFilter[]) => void;
}

interface IFilterRowProps {
  schema: IDataSchema;
  row: ICellFilter;
  index: number;
  removeSeries: (index: number) => void;
  updateItemsSeries: (index: number, field: keyof ICellFilter, value: any) => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};



const FilterRow = ({ row, index, removeSeries, updateItemsSeries, schema }: IFilterRowProps): ReactElement => {
  const [ values, setValues ] = useState<string[]>([]);

  useEffect(() => {
    setValues(schema.filter[row.field] ?? []);
  }, [ row ]);

  const updateField = (value: string) => {
    setValues(schema.filter[value]);
    updateItemsSeries(index, "field", value);
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell>
        <Select
          variant="standard"
          className="w-100"
          value={row.field}
          displayEmpty={true}
          onChange={(event) => updateField(event.target.value as string)}>
          {Object.keys(schema.filter).map(field => <MenuItem key={`field-${index}-${field}`} value={field}>{field}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            labelId="demo-mutiple-chip-label"
            id="demo-mutiple-chip"
            multiple={true}
            value={row.values}
            onChange={event => updateItemsSeries(index, "values",  event.target.value as string[])}
            input={<Input id="select-multiple-chip" />}
            renderValue={(item) =>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {(item as string[]).map((value) => <Chip key={`item-${value}`} label={value} sx={{ margin: "2px" }} />)}
              </div>
            }
            MenuProps={MenuProps}>
            {values.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell  sx={{ padding: "8px" }}>
        <IconButton sx={{ padding: "2px" }} onClick={() => removeSeries(index)} size={"small"}>
          <Tooltip title={"Remove series"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>
    </StyledTableRow>);
};

const FilterTable = ({ filters, updateFilter, schema }: IProps): ReactElement => {


  const addSeries = () => {
    updateFilter([ ...filters, { field: "", values: [] } ]);
  };

  const removeSeries = (index: number) => {
    const newFilters = [ ...filters ];
    newFilters.splice(index, 1);
    updateFilter(newFilters);
  };

  const updateItemsSeries = (index: number, field: keyof ICellFilter, value: any) => {
    const newFilters = [ ...filters ];
    const item = newFilters[index];
    item[field] = value;

    if (field === "field") {
      item.values = [];
    }

    updateFilter(newFilters);
  };

  return (
    <TableContainer sx={{ maxHeight: 315 }}>
      <Table stickyHeader={true} size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ minWidth: 250, padding: "8px" }}>
              <IconButton sx={{ padding: "2px"  }} onClick={addSeries} disabled={filters.length > 3} size={"small"}>
                <Tooltip title={"Add Filter"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
            Field
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 356 }}>Values</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}/>
          </TableRow>
        </TableHead>
        <TableBody>
          {filters.map((row, index) => {
            return (
              <FilterRow key={`field-row-${index}`}
                schema={schema}
                row={row}
                index={index}
                removeSeries={removeSeries}
                updateItemsSeries={updateItemsSeries}/>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FilterTable;
